import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule, ModalController } from '@ionic/angular';

import { AccountManagerService } from 'src/shared/services/account-manager.service';
import { DemoAccountService } from 'src/shared/services/demo-account.service';
import { Util } from 'src/shared/util/util';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';

/**
 * Prompt shown when a user opens a feature that ships in the UI but isn't enabled for
 * their account yet ("vaporware" gate) — e.g. adding a Zillow long-term listing.
 *
 * Visually a sibling of {@link DemoBlockModalComponent} (same card, icon, animation).
 *
 * Rental accounts get the account manager with Email / Text / Schedule actions — the
 * reach-out paths from the My Account page, so the two read as the same person.
 *
 * Every other account type gets a generic "contact us" prompt that hands off to the chat.
 * Only rentals have an account-manager relationship; surfacing one to a homeowner sent
 * them to a salesperson with cleaning, cleaner-confirmation and receipt questions. The
 * manager is not fetched at all in that case, so their name / email / phone never reach
 * the client.
 */
@Component({
  selector: 'feature-gate-modal',
  templateUrl: './feature-gate-modal.component.html',
  styleUrls: ['./feature-gate-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, TidyUiComponentsModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FeatureGateModalComponent implements OnInit {

  private modalController = inject(ModalController);
  private accountManagerService = inject(AccountManagerService);
  private util = inject(Util);
  private cdr = inject(ChangeDetectorRef);

  // Refreshed from /me on every load (see Me.load). The `airbnb` flag is deliberately NOT
  // consulted: nothing ever clears it, so it keeps reporting rental long after an account
  // switches to residential — exactly the case this gate has to get right.
  readonly isRentalClient = localStorage.getItem('isRentalClient') === 'true';

  readonly title = this.isRentalClient
    ? 'Please contact your account manager to use this feature'
    : 'Please contact us to use this feature';

  managerName = AccountManagerService.DEFAULT.name;
  managerPhoto = AccountManagerService.DEFAULT.photoUrl;
  managerEmail = AccountManagerService.DEFAULT.email;
  managerPhone = AccountManagerService.DEFAULT.phone;

  async ngOnInit(): Promise<void> {
    if (!this.isRentalClient) {
      return;
    }
    const manager = await this.accountManagerService.getDisplay();
    this.managerName = manager.name;
    this.managerPhoto = manager.photoUrl;
    this.managerEmail = manager.email;
    this.managerPhone = manager.phone;
    this.cdr.markForCheck();
  }

  email = () => {
    window.location.href = `mailto:${this.managerEmail}`;
  };

  text = () => {
    window.location.href = `sms:${this.managerPhone}`;
  };

  schedule = () => {
    this.util.openUrl(DemoAccountService.SCHEDULE_CALL_URL);
  };

  // Dismiss with a result and let FeatureGateService route, mirroring DemoBlockModal —
  // navigating from inside a live modal strands it on top of the new page.
  contactUs = () => {
    this.modalController.dismiss({ contactUs: true });
  };

  dismiss = () => {
    this.modalController.dismiss();
  };
}
